import { useEffect, useMemo, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Card,
  List,
  Alert,
  Skeleton,
  Tag,
  Avatar,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy, faStar, faMedal } from "@fortawesome/free-solid-svg-icons";
import { getLoyaltyHistory } from "../repository/data.repository";
import { LoyaltyHistoryEntry, MAX_PAGE_SIZE } from "../repository/data.default";

interface LeaderRow {
  userUid: string;
  displayName: string;
  points: number;
}

// PD-02 §6.16 Actions — Points System / Leadership Board. Aggregates the
// company's loyalty ledger (/LoyaltyHistory is scoped to the company for
// AppCompanyUser) and ranks employees by net points earned.
function LeadershipBoardPage() {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<LoyaltyHistoryEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLoyaltyHistory({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    })
      .then((data) => {
        if (!cancelled) setEntries(data.data ?? []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const leaders: LeaderRow[] = useMemo(() => {
    const sumByUser = new Map<string, LeaderRow>();
    for (const e of entries) {
      const key = e.userUid;
      const display =
        [e.userFirstName, e.userLastName].filter(Boolean).join(" ") ||
        e.userUserName ||
        "Unknown";
      const current = sumByUser.get(key) ?? {
        userUid: key,
        displayName: display,
        points: 0,
      };
      current.points += e.pointsChange ?? 0;
      current.displayName = display;
      sumByUser.set(key, current);
    }
    return Array.from(sumByUser.values())
      .filter((r) => r.points !== 0)
      .sort((a, b) => b.points - a.points)
      .slice(0, 20);
  }, [entries]);

  return (
    <Col className="animate__animated animate__fadeIn flex flex-auto flex-col gap-4 overflow-y-auto p-6">
      <Row>
        <Typography.Title level={3} className="!m-0">
          <FontAwesomeIcon icon={faTrophy} className="mr-2" />
          Leadership Board
        </Typography.Title>
      </Row>

      <Alert
        type="info"
        showIcon
        message="Top 20 employees by points earned, derived from the loyalty ledger. The master badge/points catalogue is still being finalised."
      />

      <Card
        className="emp-card"
        title="Top employees by points"
        bodyStyle={{ padding: 0 }}
      >
        {loading ? (
          <div className="p-4">
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        ) : leaders.length === 0 ? (
          <div className="p-4">
            <Typography.Text type="secondary">
              No points earned yet. Once your employees start completing
              assessments and other actions their rank will appear here.
            </Typography.Text>
          </div>
        ) : (
          <List
            dataSource={leaders}
            renderItem={(row, index) => {
              const rank = index + 1;
              const medal =
                rank === 1
                  ? { color: "gold", icon: faTrophy, label: "🥇" }
                  : rank === 2
                  ? { color: "default", icon: faMedal, label: "🥈" }
                  : rank === 3
                  ? { color: "orange", icon: faMedal, label: "🥉" }
                  : null;
              return (
                <List.Item
                  style={{ paddingLeft: 16, paddingRight: 16 }}
                  actions={[
                    <Tag color="gold" icon={<FontAwesomeIcon icon={faStar} />}>
                      {row.points} pts
                    </Tag>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar style={{ backgroundColor: "#14a098" }}>
                        {rank}
                      </Avatar>
                    }
                    title={
                      <span>
                        {medal && <span className="mr-1">{medal.label}</span>}
                        {row.displayName}
                      </span>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Card>
    </Col>
  );
}

export default LeadershipBoardPage;
